import { hiddenField } from '../dom';
import { findStep } from './graph';

export default function serialize(workflow) {
  const nodes = [...workflowFields(workflow)];

  for (const trigger of workflow.triggers) nodes.push(...triggerFields(trigger, workflow));
  for (const step of workflow.steps) nodes.push(...stepFields(step, workflow));
  for (const variable of workflow.variables) nodes.push(...variableFields(variable));
  for (const input of workflow.inputs) nodes.push(...inputFields(input));

  return nodes;
}

function workflowFields({ name, description, concurrency }) {
  return fields('workflow', {
    name,
    description,
    concurrency_mode: concurrency.mode,
    concurrency_key: concurrency.mode === 'off' ? '' : concurrency.key,
  });
}

function triggerFields(trigger, workflow) {
  const listening = trigger.kind === 'webhook';

  return [
    ...fields('workflow[triggers][]', {
      id: persistedId(trigger.id),
      name: trigger.name,
      kind: trigger.kind,
      webhook_id: listening ? trigger.webhookId : '',
      events: listening ? trigger.events : '',
      conditions: listening ? trigger.conditions : '',
      cron_expression: listening ? '' : trigger.cron,
      canvas_x: trigger.canvasX ?? '',
      canvas_y: trigger.canvasY ?? '',
    }),
    hiddenField('workflow[triggers][][step_keys][]', ''),
    ...keyFields('workflow[triggers][][step_keys][]', trigger.starts, workflow),
  ];
}

function stepFields(step, workflow) {
  return [
    ...fields('workflow[steps][]', {
      id: persistedId(step.id),
      key: step.key,
      name: step.name,
      mode: step.mode,
    }),
    ...(step.mode === 'workflow' ? [] : step.bundles).flatMap((attachment) =>
      fields('workflow[steps][][bundles][]', {
        bundle_id: attachment.bundleId,
        revision_number: attachment.revisionNumber ?? '',
      })
    ),
    ...(step.mode === 'agent' ? step.mcpServers : []).flatMap((attachment) =>
      fields('workflow[steps][][mcp_servers][]', {
        mcp_server_id: attachment.mcpServerId,
        canvas_x: attachment.canvasX ?? '',
        canvas_y: attachment.canvasY ?? '',
      })
    ),
    ...fields('workflow[steps][]', {
      command: step.mode === 'script' ? step.command : '',
      credential_id: step.mode === 'agent' ? (step.credentialId ?? '') : '',
      prompt: step.mode === 'agent' ? step.prompt : '',
      conditions: step.conditions,
      instructions: step.mode === 'gate' ? step.instructions : '',
      interruptible: step.mode === 'agent' ? Boolean(step.interruptible) : false,
      child_workflow_id: step.mode === 'workflow' ? step.childWorkflowId : '',
      child_inputs: step.mode === 'workflow' ? step.childInputs : '',
      each: step.mode === 'workflow' ? step.each : '',
      allow_failures: step.mode === 'workflow' ? Boolean(step.allowFailures) : false,
      canvas_x: step.canvasX ?? '',
      canvas_y: step.canvasY ?? '',
    }),
    ...keyFields('workflow[steps][][depends_on_keys][]', step.dependsOn, workflow),
  ];
}

function variableFields(variable) {
  return fields('workflow[environment_variables][]', {
    id: persistedId(variable.id),
    key: variable.key,
    value: variable.value,
    step_key: variable.stepKey,
  });
}

function inputFields(input) {
  return fields('workflow[inputs][]', {
    id: persistedId(input.id),
    key: input.key,
    label: input.label,
    kind: input.kind,
    required: input.required,
    default_value: input.defaultValue,
    options: input.kind === 'select' ? input.options : '',
    hint: input.hint,
  });
}

function fields(prefix, values) {
  return Object.entries(values).map(([name, value]) => hiddenField(`${prefix}[${name}]`, value));
}

function keyFields(name, ids, workflow) {
  return ids
    .map((id) => findStep(workflow, id)?.key)
    .filter(Boolean)
    .map((key) => hiddenField(name, key));
}

function persistedId(id) {
  return id.startsWith('new-') ? '' : id;
}
